import React, { ReactNode } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { autoUpdate } from '@floating-ui/react';
import { useDismiss } from '@floating-ui/react';
import { useInteractions } from '@floating-ui/react';
import { flip } from '@floating-ui/react';
import { offset } from '@floating-ui/react';
import { useFloating } from '@floating-ui/react';

export interface HorizontalFilterBarProps {
  /**
   * The children to render inside the horizontal filter bar
   */
  items: HorizontalFilterBarItemProps[] | HorizontalFilterBarItemProps[][];

  slotRight?: ReactNode;
}

export interface HorizontalFilterBarItemProps {
  /**
   * The content to render inside the horizontal filter bar
   * @param params
   */
  content: (params: {
    openMenu: () => void;
    menuOpened: boolean;
    disabled?: boolean;
  }) => React.ReactNode;

  /**
   * The menu to render when the item is clicked
   * @param params
   */
  menu: (params: { closeMenu: () => void }) => React.ReactNode;

  /**
   * The key of the item
   */
  key: string;
}

export const HorizontalFilterBar = ({
  items,
  slotRight,
}: HorizontalFilterBarProps) => {
  const [openedItem, setOpenedItem] = React.useState<string | null>(null);

  const groupItems: HorizontalFilterBarItemProps[][] =
    items.length > 0 && Array.isArray(items[0])
      ? (items as HorizontalFilterBarItemProps[][])
      : ([items] as HorizontalFilterBarItemProps[][]);

  // If there are no items to display, return an empty fragment
  if (
    groupItems.length === 0 ||
    groupItems.every((group) => group.length === 0)
  ) {
    return <></>;
  }

  return (
    <div
      data-testid="filter-bar"
      className={'bg-[#FBFBFB] rounded-lg p-3 shadow-2px w-full'}
    >
      <div className="flex gap-3 flex-col justify-between md:flex-row">
        {groupItems.map((group, index) => (
          <Spacing
            direction={'horizontal'}
            size={''}
            className={'flex-wrap'}
            key={`group-${index}`}
          >
            {group.map((item) => (
              <HorizontalFilterBarItem
                {...item}
                key={item.key}
                openMenu={() => setOpenedItem(item.key)}
                closeMenu={() => setOpenedItem(null)}
                menuOpened={openedItem === item.key}
              />
            ))}
          </Spacing>
        ))}

        {slotRight}
      </div>
    </div>
  );
};

export interface HorizontalFilterBarItemExtraProps {
  /**
   * Whether the menu is open or not
   */
  menuOpened: boolean;

  /**
   * Callback to open the menu
   */
  openMenu: () => void;

  /**
   * Callback to close the menu
   */
  closeMenu: () => void;

  /**
   * Indicates if the menu button is disabled
   */
  disabled?: boolean;
}

export const HorizontalFilterBarItem = ({
  menu,
  content,
  menuOpened,
  openMenu,
  closeMenu,
  disabled,
}: HorizontalFilterBarItemProps & HorizontalFilterBarItemExtraProps) => {
  const { refs, floatingStyles, context } = useFloating({
    open: menuOpened,
    placement: 'bottom-start',
    transform: false,
    middleware: [
      offset(0),
      flip({
        fallbackPlacements: ['bottom-end'],
      }),
    ],
    whileElementsMounted: autoUpdate,
    onOpenChange(nextOpen) {
      /**
       * onOpenChange is going to be called whenever floating ui expects the
       * menu to be closed
       */
      nextOpen ? openMenu() : closeMenu();
    },
  });

  // dismiss hook is necessary to close the menu whenever a user clicks outside
  // or presses the ESC key
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const renderedContent = content({
    openMenu,
    menuOpened,
    disabled,
  });

  if (!renderedContent) {
    return null;
  }

  return (
    <div>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {renderedContent}
      </div>

      {menuOpened && (
        <div
          data-testid="filter-menu"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <div className={'relative z-overlay-cover'}>
            {menu({ closeMenu })}
          </div>
        </div>
      )}
    </div>
  );
};
